import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { BarsTransition } from '@tanium/react-loading-transition';
import { Stack } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { Box } from '@tanium/react-box';
import IDropdownOption from 'common/IDropdownOption';
import {
  TARGET_ID,
  TARGET_NAME,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import AwsAccountObjectSelectors from 'components/pages/console/store/AwsAccountObjectSelectors';
import AwsAccountObjectSlice from 'components/pages/console/store/AwsAccountObjectSlice';
import AwsAccountObject from 'gql/AwsAccountObject';
import { isProcessing } from 'store/ActionState';
import Tooltip from 'components/tooltip/Tooltip';

export const AWSAccount = (): React.ReactElement => {
  const dispatch = useDispatch();
  const awsAccountObjects = useSelector(AwsAccountObjectSelectors.selectAwsAccountObjects);
  const fetchAwsAccountObjectsState = useSelector(AwsAccountObjectSelectors.selectFetchAwsAccountObjectsState);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();

  const formInputIdPrefix = 'create-request-';

  useEffect(() => {
    dispatch(AwsAccountObjectSlice.actions.fetchAllAwsAccountObjects({ viewAllRoles: false }));
  }, [dispatch]);

  const accountOptions = (accounts: AwsAccountObject[]): IDropdownOption[] =>
    accounts.map((awsAccountObject) => {
      return {
        label: awsAccountObject.name,
        value: awsAccountObject.id,
        id: awsAccountObject.awsAccountID,
        description: awsAccountObject.description,
        alias: awsAccountObject.awsAccountAlias,
      };
    });

  // Initializing targetId value
  useEffect(() => {
    if (targetIdQuery) {
      void formik.setFieldValue(TARGET_ID, targetIdQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormSection title={t('SELECT_AWS_ACCOUNT')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('AWS_ACCOUNT')}
            required
            description={t('AWS_ACCOUNT_DESCRIPTION')}
            errors={
              (get(form.touched, field.name) &&
                /* istanbul ignore next */
                get(form.errors, field.name)) as FormikErrors<string>
            }
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {<BarsTransition isLoading={isProcessing(fetchAwsAccountObjectsState)} />}
            {!isProcessing(fetchAwsAccountObjectsState) && awsAccountObjects && (
              <FormSelectInput
                placeholder={t('AWS_ACCOUNT_PLACEHOLDER')}
                onChange={(option): void => {
                  void formik.setFieldValue(TARGET_ID, option.value);
                  void formik.setFieldValue(TARGET_NAME, option.label);
                  void formik.setFieldValue(ACCESS_TYPE, '');
                  void formik.setFieldValue(ACCESS_TYPES, []);
                  setTargetIdQuery(option.value);
                  setAccessTypeQuery(undefined);
                }}
                value={accountOptions(awsAccountObjects).find((option) => option.value === field.value)}
                inputId={`${formInputIdPrefix}${field.name}`}
                options={accountOptions(awsAccountObjects)}
                aria-label={t('AWS_ACCOUNT')}
                formatOptionLabel={(
                  option: {
                    value: string;
                    label: string;
                    id: string;
                    alias: string;
                    description?: string;
                  },
                  state
                ) => {
                  const isSelected =
                    state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                  /* istanbul ignore next */
                  const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                  return (
                    <Stack direction="column" itemSpacing={0.5}>
                      <Box display="inline-flex">
                        <Box mr={1}>{option.label}</Box>
                        <Box mr={1} color={subtextColor} fontSize={11}>
                          {t('ALIAS')}: {option.alias}
                        </Box>
                        <Box color={subtextColor} fontSize={11}>
                          {t('ID')}: {option.id}
                        </Box>
                      </Box>
                      <Tooltip content={option.description}>
                        <Box color={subtextColor} fontSize={11}>
                          {option.description}
                        </Box>
                      </Tooltip>
                    </Stack>
                  );
                }}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    marginTop: theme.space[1],
                    marginBottom: theme.space[1],
                    position: 'relative',
                    transform: 'none',
                    whiteSpace: 'normal',
                    maxHeight: '3.75em',
                  }),
                  input: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: theme.space[1] - 2,
                    paddingTop: 0,
                  }),
                }}
              />
            )}
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default AWSAccount;
